import { Activity, Check, Copy, DollarSign, Users, Volume1, VolumeX } from "lucide-react"
import { useEffect, useState } from "react"
import footerConfig from "../../data/footer-config.json"
import { formatCount, formatMarketCap, truncateTitle, useFooterStats } from "../../hooks/use-footer-stats"
import { FooterCommunityLink, FooterSocialLinks } from "./FooterLinks"
import { LandingMicroFooterDivider } from "./LandingMicroFooterDivider"
import { playAudioToggleSound, setGlobalMute } from "./LandingSounds"
import { LandinguseBrackyPrice } from "./LandinguseBrackyPrice"

// Compact live trading footer displayed on md+ viewport widths
export function LandingMicroFooter() {
  const { stats, setStats } = useFooterStats()
  const brackyAddress = footerConfig.contracts.bracky

  const handleCopy = () => {
    navigator.clipboard
      .writeText(brackyAddress)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {
        /* Clipboard write failed, silently ignore */
      })
  }

  const [isAudioOn, setIsAudioOn] = useState(() => {
    // Initialize from localStorage, default to true
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("bracket:audio-enabled")
      return saved !== null ? saved === "true" : true
    }
    return true
  })
  const [copied, setCopied] = useState(false)
  const [isAtTop, setIsAtTop] = useState(true)

  // Scroll detection to adjust background opacity
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      setIsAtTop(scrollTop <= 10) // Consider "at top" if within 10px of top
    }

    // Set initial state
    handleScroll()

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Initialize global mute state to match component state
  useEffect(() => {
    setGlobalMute(!isAudioOn)
  }, [isAudioOn])

  const toggleAudio = () => {
    // Play the toggle sound before changing state (always play this for user feedback)
    playAudioToggleSound()

    const newAudioState = !isAudioOn
    setIsAudioOn(newAudioState)

    // Save to localStorage
    localStorage.setItem("bracket:audio-enabled", newAudioState.toString())

    // Set global mute state for all sounds from LandingSounds.ts
    setGlobalMute(!newAudioState)

    // If the user just muted audio, immediately stop any currently playing track
    if (!newAudioState && typeof window !== "undefined" && window.activeCallAudio) {
      try {
        window.activeCallAudio.pause()
        window.activeCallAudio.currentTime = 0
      } catch {
        /* Safely ignore errors if AudioElement is not in a playable state */
      }
      ;(window as { activeCallAudio?: HTMLAudioElement | undefined }).activeCallAudio = undefined
    }
  }

  // On mount & whenever the global audio state switches to muted externally, ensure no audio keeps playing.
  useEffect(() => {
    if (!isAudioOn && typeof window !== "undefined" && window.activeCallAudio) {
      try {
        window.activeCallAudio.pause()
        window.activeCallAudio.currentTime = 0
      } catch {
        /* ignore */
      }
      ;(window as { activeCallAudio?: HTMLAudioElement | undefined }).activeCallAudio = undefined
    }
  }, [isAudioOn])

  // Fetch live BRACKY price & 24h change
  const { data: brackyData } = LandinguseBrackyPrice()

  // Update holder count when fresh data arrives
  useEffect(() => {
    if (typeof brackyData?.holders === "number" && brackyData.holders > 0) {
      setStats((prev) => ({ ...prev, holders: brackyData.holders }))
    }
  }, [brackyData, setStats])

  return (
    <>
      <div
        className={`hidden md:block fixed bottom-0 left-0 right-0 bg-linear-to-t h-[3.84rem] text-white text-[0.79rem] z-600 transition-opacity duration-300 ${
          isAtTop
            ? "from-[#030308]/50 via-[#030308]/35 to-[#030308]/0"
            : "from-[#030308] via-[#030308]/70 to-[#030308]/0"
        }`}
      >
        <div className="max-w-7xl mx-auto px-[1.44rem] py-[0.48rem]">
          {/* (Content moved to centered toolbar for baseline alignment) */}
        </div>
      </div>

      {/* Fixed left stats toolbar */}
      <div className="hidden md:flex fixed bottom-[0.48rem] left-[1.2rem] items-center gap-[0.62rem] z-601">
        {/* Live badge styled same as BracketCard */}
        <div className="relative flex items-center justify-center px-[0.72rem] py-[0.12rem] rounded-full border border-white/10">
          <div className="relative mr-[0.36rem]">
            <div className="w-2 h-2 rounded-full bg-[#22FF65]" />
            <div className="absolute inset-0 w-2 h-2 rounded-full bg-[#22FF65] animate-ping opacity-75" />
          </div>
          <span className="text-[#22FF65] text-[0.84rem] tracking-[.025rem]">LIVE</span>
        </div>

        {/* Trending Brackt Title */}
        <div className="flex items-center text-[0.84rem] gap-[0.24rem]">
          <span className="text-white/65 md:hidden lg:block">24h Vol:</span>
          <span className="mr-.5">🏈</span>
          <a
            href={footerConfig.links.twitterReplies}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold shiny-text-trending"
          >
            {truncateTitle("2025 NFL Playoff Race")}
          </a>
        </div>

        {/* Divider */}
        <LandingMicroFooterDivider className="mx-[.05rem]" />

        {/* Trades (Moves) metric */}
        <div className="flex items-center gap-[0.24rem] text-white/65 text-[0.84rem] md:hidden lg:flex">
          <Activity className="w-[0.88rem] h-[0.88rem]" />
          <span>152.1K Live Bets</span>
        </div>

        {/* Divider between Trades and In The Arena */}
        <LandingMicroFooterDivider className="md:hidden lg:block mx-[.05rem]" />

        {/* In The Arena metric */}
        <div className="hidden md:flex items-center gap-[0.24rem] text-white/65 text-[0.84rem]">
          <Users className="w-[0.88rem] h-[0.88rem]" />
          <span>{formatCount(stats.holders)} Global Network</span>
        </div>

        {/* Divider following In The Arena */}
        <LandingMicroFooterDivider className="hidden md:block mx-[.05rem]" />

        {/* $BRACKY market cap */}
        <div className="relative flex items-center gap-[0.24rem] text-[0.84rem]">
          <button
            type="button"
            aria-label="Copy BRACKY contract address"
            onClick={handleCopy}
            className="text-white/65 hover:text-white active:opacity-80"
          >
            {copied ? (
              <Check className="w-[0.88rem] h-[0.88rem] text-[#22FF65]" />
            ) : (
              <Copy className="w-[0.86rem] h-[0.86rem]" />
            )}
          </button>
          <a
            href="https://www.geckoterminal.com/base/pools/0x2e8b5cf35680f8b7df0957d05c6a0a4ae1d00cde"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-[0.048rem]"
          >
            <DollarSign className="w-[0.92rem] h-[0.92rem] text-[#22FF65]" />
            <span className="text-white font-medium">
              {copied ? "CA Copied!" : "BRACKY"}
              {!copied && ":"} {formatMarketCap(brackyData?.marketCapUsd ?? 0)}
            </span>
          </a>
        </div>
      </div>

      {/* Fixed right toolbar: Support, socials, Built on Base & volume */}
      <div className="hidden md:flex fixed bottom-[0.72rem] right-[0.96rem] items-center gap-3 z-601">
        {/* Community */}
        <FooterCommunityLink />

        {/* Divider */}
        <LandingMicroFooterDivider className="h-4 hidden lg:block mx-[.05rem]" />

        {/* Socials */}
        <FooterSocialLinks />

        {/* Divider */}
        <LandingMicroFooterDivider className="hidden md:block mx-[.05rem]" />

        {/* Built on Base */}
        <div className="hidden xl:flex items-center gap-[0.48rem] text-white/90 text-[0.84rem] font-medium select-none pointer-events-none">
          <span className="inline-block w-[0.836rem] h-[0.836rem] bg-[#0016f5] rounded-[2px] translate-y-[0.05rem]" />
          <span className="text-white/65 -mr-[0.14rem]">Built on</span>
          <span className="font-medium text-white">Base</span>
        </div>

        {/* Divider */}
        <LandingMicroFooterDivider className="md:hidden xl:block mx-[.05rem]" />

        {/* Controls */}
        <div className="flex items-center gap-[0.48rem]">
          {/* Volume toggle */}
          <button
            type="button"
            aria-label="Toggle audio"
            onClick={toggleAudio}
            className="text-white/65 hover:text-white active:opacity-80 transition-colors"
          >
            {isAudioOn ? (
              <Volume1 className="w-[1.11rem] h-[1.11rem]" />
            ) : (
              <VolumeX className="w-[1.11rem] h-[1.11rem]" />
            )}
          </button>
        </div>
      </div>
    </>
  )
}
